import * as React from 'react';

import {
  Button,
  Checkbox,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogMain,
  DialogOverlay,
  DialogTitle,
  Money,
  NumberField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { BanknoteArrowUp, FileText, Loader2 } from 'lucide-react';

import { UnappliedAdvanceForBooking } from '../../_graphql/queryUnappliedAdvancesForAccounts';
import {
  $advancesByAccount,
  $advancesLoading,
  $applySelectedAdvances,
  $closeApplyAdvancesDialog,
  $hasAdvancesAvailable,
  $invoicesByAccount,
  $isApplyAdvancesDialogOpen,
  $isApplyingAdvances,
  $selectedAdvanceApplications,
  $selectedCount,
  $toggleAdvanceSelection,
  $updateAdvanceAmount,
  CreatedInvoiceForAdvances,
} from '../../_state/bookingApplyAdvances.state';
import { useTranslations } from '../../i18n/useTranslations';

type CustomerAdvanceSectionProps = {
  accountId: string;
  accountName: string;
  invoices: CreatedInvoiceForAdvances[];
  advances: UnappliedAdvanceForBooking[];
};

const CustomerAdvanceSection: React.FC<CustomerAdvanceSectionProps> = ({
  accountId: _accountId,
  accountName,
  invoices,
  advances,
}) => {
  const t = useTranslations();
  const selections = useAtomValue($selectedAdvanceApplications);
  const toggleSelection = useSetAtom($toggleAdvanceSelection);
  const updateAmount = useSetAtom($updateAdvanceAmount);

  // For v1, we'll apply advances to the first invoice for this customer
  const targetInvoice = invoices[0];
  if (!targetInvoice) return null;

  const totalInvoiceAmount = invoices.reduce((sum, inv) => sum + inv.amount, 0);

  // Filter advances to only show those matching the target invoice currency
  const matchingAdvances = advances.filter(
    (adv) => adv.unappliedAmount.currencyCode === targetInvoice.currencyCode,
  );
  const hasNonMatchingAdvances = advances.length > matchingAdvances.length;

  return (
    <div className="@CustomerAdvanceSection rounded-lg border border-neutral/20 p-4">
      {/* Customer Header */}
      <div className="mb-3 flex items-center gap-2">
        <span className="font-semibold">{accountName}</span>
      </div>

      {/* Invoices */}
      <div className="mb-4 rounded bg-neutral/5 p-3">
        <div className="mb-2 flex items-center gap-2 text-sm font-medium text-neutral/70">
          <FileText className="size-4" />
          {invoices.length === 1
            ? t.bookingApplyAdvances.invoice
            : `${t.bookingApplyAdvances.invoice}s`}
        </div>
        {invoices.map((invoice) => (
          <div
            key={invoice.invoiceId}
            className="flex items-center justify-between text-sm"
          >
            <span>{invoice.documentNumber}</span>
            <span className="font-medium">
              <Money
                amount={invoice.amount}
                currencyCode={invoice.currencyCode}
              />
            </span>
          </div>
        ))}
        {invoices.length > 1 && (
          <div className="mt-2 flex items-center justify-between border-t border-neutral/10 pt-2 text-sm font-semibold">
            <span>{t.shared.total}</span>
            <span>
              <Money
                amount={totalInvoiceAmount}
                currencyCode={targetInvoice.currencyCode}
              />
            </span>
          </div>
        )}
      </div>

      {/* Available Advances */}
      <div>
        <div className="mb-2 flex items-center gap-2 text-sm font-medium text-neutral/70">
          <BanknoteArrowUp className="size-4" />
          {t.bookingApplyAdvances.availableAdvances}
        </div>
        {matchingAdvances.length === 0 ? (
          <div className="rounded bg-neutral/5 p-3 text-sm text-neutral/60">
            {hasNonMatchingAdvances
              ? t.bookingApplyAdvances.noMatchingCurrency(
                  targetInvoice.currencyCode,
                )
              : t.bookingApplyAdvances.noAdvancesForCustomer}
          </div>
        ) : (
          <div className="space-y-2">
            {matchingAdvances.map((advance) => {
              const key = `${advance.id}-${targetInvoice.invoiceId}`;
              const isSelected = selections.has(key);
              const selection = selections.get(key);

              // Calculate suggested amount: min of advance unapplied and target invoice amount
              const suggestedAmount = Math.min(
                advance.unappliedAmount.amount,
                targetInvoice.amount,
              );

              return (
                <div
                  key={advance.id}
                  className="flex items-center gap-3 rounded border border-neutral/10 p-2"
                >
                  <Checkbox
                    isSelected={isSelected}
                    onChange={() => {
                      toggleSelection({
                        advanceId: advance.id,
                        invoiceId: targetInvoice.invoiceId,
                        amount: suggestedAmount,
                        currencyCode: advance.unappliedAmount.currencyCode,
                      });
                    }}
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">
                        {advance.documentNumber}
                      </span>
                      <span className="text-sm text-neutral/60">
                        <Money
                          amount={advance.unappliedAmount.amount}
                          currencyCode={advance.unappliedAmount.currencyCode}
                        />{' '}
                        {t.bookingApplyAdvances.available}
                      </span>
                    </div>
                  </div>
                  {isSelected && (
                    <div className="w-32">
                      <NumberField
                        aria-label={t.bookingApplyAdvances.applyAmount}
                        value={selection?.amount ?? suggestedAmount}
                        onChange={(value) => {
                          updateAmount({
                            advanceId: advance.id,
                            invoiceId: targetInvoice.invoiceId,
                            amount: value,
                          });
                        }}
                        minValue={0.01}
                        maxValue={Math.min(
                          advance.unappliedAmount.amount,
                          targetInvoice.amount,
                        )}
                        formatOptions={{
                          style: 'currency',
                          currency: advance.unappliedAmount.currencyCode,
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

type BookingApplyAdvancesDialogProps = {
  onComplete?: () => void;
};

export const BookingApplyAdvancesDialog: React.FC<
  BookingApplyAdvancesDialogProps
> = ({ onComplete }) => {
  const t = useTranslations();
  const toast = useToast();

  const isOpen = useAtomValue($isApplyAdvancesDialogOpen);
  const isLoading = useAtomValue($advancesLoading);
  const isApplying = useAtomValue($isApplyingAdvances);
  const hasAdvances = useAtomValue($hasAdvancesAvailable);
  const selectedCount = useAtomValue($selectedCount);
  const invoicesByAccount = useAtomValue($invoicesByAccount);
  const advancesByAccount = useAtomValue($advancesByAccount);

  const closeDialog = useSetAtom($closeApplyAdvancesDialog);
  const applyAdvances = useSetAtom($applySelectedAdvances);

  const handleSkip = React.useCallback(() => {
    closeDialog();
    onComplete?.();
  }, [closeDialog, onComplete]);

  const handleApply = React.useCallback(async () => {
    try {
      const result = await applyAdvances();

      if (result.failed.length === 0 && result.success.length > 0) {
        toast.success(
          t.bookingApplyAdvances.success.replace(
            '{count}',
            String(result.success.length),
          ),
        );
      } else if (result.failed.length > 0 && result.success.length > 0) {
        toast.warning(
          t.bookingApplyAdvances.partialSuccess
            .replace('{success}', String(result.success.length))
            .replace(
              '{total}',
              String(result.success.length + result.failed.length),
            )
            .replace('{failed}', String(result.failed.length)),
        );
      } else if (result.failed.length > 0 && result.success.length === 0) {
        toast.error(t.bookingApplyAdvances.error);
      }

      closeDialog();
      onComplete?.();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t.bookingApplyAdvances.error,
      );
    }
  }, [applyAdvances, toast, t, closeDialog, onComplete]);

  if (!isOpen) {
    return null;
  }

  // Build list of customers with advances
  const customersWithAdvances: Array<{
    accountId: string;
    accountName: string;
    invoices: CreatedInvoiceForAdvances[];
    advances: UnappliedAdvanceForBooking[];
  }> = [];

  for (const [accountId, invoices] of invoicesByAccount) {
    const advances = advancesByAccount.get(accountId);
    if (advances && advances.length > 0) {
      customersWithAdvances.push({
        accountId,
        accountName: invoices[0]?.accountName ?? 'Unknown',
        invoices,
        advances,
      });
    }
  }

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={(open) => !open && handleSkip()}
      className="@BookingApplyAdvancesDialog"
    >
      <DialogContent className="max-h-[90vh] w-[600px] max-w-[90vw]">
        <DialogHeader>
          <DialogTitle>{t.bookingApplyAdvances.title}</DialogTitle>
        </DialogHeader>

        <DialogMain className="flex flex-col gap-4 overflow-y-auto px-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="size-6 animate-spin text-neutral/60" />
            </div>
          ) : !hasAdvances || customersWithAdvances.length === 0 ? (
            <div className="rounded-lg bg-neutral/5 p-6 text-center">
              <BanknoteArrowUp className="mx-auto mb-2 size-8 text-neutral/40" />
              <p className="text-neutral/60">
                {t.bookingApplyAdvances.noAdvancesAvailable}
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-neutral/70">
                {t.bookingApplyAdvances.description}
              </p>
              <div className="space-y-4">
                {customersWithAdvances.map((customer) => (
                  <CustomerAdvanceSection
                    key={customer.accountId}
                    accountId={customer.accountId}
                    accountName={customer.accountName}
                    invoices={customer.invoices}
                    advances={customer.advances}
                  />
                ))}
              </div>
            </>
          )}
        </DialogMain>

        <DialogFooter className="flex justify-end gap-2 px-4 py-4">
          <Button variant="text" color="neutral" onPress={handleSkip}>
            {hasAdvances && customersWithAdvances.length > 0
              ? t.bookingApplyAdvances.skip
              : t.bookingApplyAdvances.close}
          </Button>
          {hasAdvances && customersWithAdvances.length > 0 && (
            <Button
              variant="solid"
              color="primary"
              onPress={handleApply}
              isDisabled={selectedCount === 0 || isApplying}
              icon={
                isApplying ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <BanknoteArrowUp className="size-4" />
                )
              }
            >
              {isApplying
                ? t.bookingApplyAdvances.applying
                : t.bookingApplyAdvances.applySelected}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
